import { Mail, TriangleAlert } from "lucide-react";

import type { ClaimWithRelations } from "../../api/use-claim";
import { useAskForAgentFeedback, useClaimContactEmails } from "../../api/use-claim-actions";
import { isPartnerFeedOffice } from "../../lib/partner-feed";
import { EligibilityInsight } from "../ai-insights/eligibility-insight";
import { ClaimGroups } from "../claim-groups";
import { TicketHistory } from "../passengers/ticket-history";
import { CtaShell } from "./cta-shell";

/**
 * Port of states-cta/ClaimValidationCTA.vue — CLAIM_VALIDATION.WAIT_FOR_ELIGIBILITY_CHECK.
 * Reduced to the verdict card + the send-cession action: e-tickets, CX and the abandon flow all
 * live in the study (card + modal), so the old inline duplicates were removed (TECH-705).
 * Exception: partner-feed claims (passive office, e.g. Digitrips) can never have a study —
 * their e-tickets are shown inline here instead.
 */
export function ClaimValidationCTA({ claim }: { claim: ClaimWithRelations }) {
  const askForAgentFeedback = useAskForAgentFeedback(claim.id);
  const { data: claimEmails } = useClaimContactEmails(claim.id);

  const reservation = claim.legalSpecification.impactedSegment.reservation;

  // Loisir : envoi à l'agent ; Affaires : envoi à l'email dédié à l'entreprise.
  const agentEmail = !claimEmails
    ? "Chargement..."
    : !reservation.travelAgencyOffice.isBusinessTravel && !reservation.travelAgencyCompany
      ? claimEmails.reservationAgentEmail || claimEmails.fallbackOfficeEmail
      : claimEmails.companyEmail || "(email entreprise vide)";

  // Check if the enterprise is deactivated
  const enterprise = reservation.travelAgencyCompany;
  const isEnterpriseDeactivated = Boolean(
    enterprise?.disabledAt && new Date(enterprise.disabledAt) <= new Date(),
  );

  function confirmClaim() {
    askForAgentFeedback.mutate(agentEmail);
  }

  return (
    <>
      {isEnterpriseDeactivated ? (
        <div className="bg-slate-50 pt-5">
          <div className="mx-auto max-w-lg rounded-md border border-amber-200 bg-amber-50 p-4 text-left">
            <div className="flex">
              <div className="shrink-0">
                <TriangleAlert className="h-5 w-5 text-amber-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">Entreprise désactivée</h3>
                <p className="mt-1 text-sm text-amber-700">
                  Cette entreprise n'est plus active. Impossible d'envoyer une demande de signature.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <CtaShell legalStepName={claim.claimLegalStep.name}>
        {/* Groups first while checking eligibility: a claim of the same wave is usually decided alike. */}
        <div className="mb-4 empty:hidden">
          <ClaimGroups claimId={claim.id} />
        </div>

        {/* AI study (étude de dossier) — verdict, résumé, étude complète et abandon si non éligible */}
        <EligibilityInsight claimId={claim.id} />

        {isPartnerFeedOffice(reservation.travelAgencyOffice.amadeusOfficeId) ? (
          <div className="mt-6 text-left">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Billets électroniques</h3>
            <p className="mt-1 text-sm text-gray-500">E-Tickets pour chaque passager et leurs statuts.</p>
            <div className="w-full py-2">
              <TicketHistory
                claimId={claim.id}
                impactedFlightId={claim.legalSpecification.impactedSegment.flightId}
              />
            </div>
          </div>
        ) : null}

        <p className="mt-4 text-sm text-gray-500">Verifier le dossier avant de l'envoyer à l'agent.</p>
        <ul className="mt-6 divide-y divide-gray-200 border-t border-b border-gray-200">
          <li>
            <div className="group pointer-events-auto relative flex items-start space-x-6 py-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">1. Envoyer la cession directement</p>
                <p className="text-sm text-gray-500">
                  Après avoir vérifié les doublons, société, circonstance extraordinaire, montant etc.
                </p>
                <p className="mt-3 text-sm text-gray-500">
                  Email envoyé à : <span className="font-medium text-blue-600">{agentEmail}</span>
                </p>
              </div>
              <div className="shrink-0 self-center">
                <button
                  type="button"
                  onClick={confirmClaim}
                  disabled={askForAgentFeedback.isPending}
                  className="inline-flex cursor-pointer items-center gap-x-1.5 rounded bg-white px-2 py-1.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-gray-300 ring-inset hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 group-hover:text-gray-900"
                >
                  <Mail className="h-4 w-4 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                  Envoyer
                </button>
              </div>
            </div>
          </li>
        </ul>
      </CtaShell>
    </>
  );
}
